import type { Editor } from '@tiptap/react';
import { toast } from 'sonner';

import { getMaImageUrl } from '@/components/image-library/image-library-utils';
import { isNodeInSchema } from '@/lib/tiptap-utils';
import { linkImageToDocument, linkLibraryImageFromSrc, type MaImage, type MaImageDocument } from '@/lib/zybooks-api';
import authService from '@/services/zybook-auth-service';

const linkPastedLibraryImageChainByEditor = new WeakMap<Editor, Promise<void>>();

export function linkedMaImageNodeAttrs(
    maImage: MaImage,
    maImageDocument: MaImageDocument | null,
): {
    src: string;
    alt: string | null;
    maImageId: number;
    maImageNodeId: string | null;
} {
    return {
        src: getMaImageUrl(maImage),
        alt: maImage.alt_text,
        maImageId: maImage.ma_image_id,
        maImageNodeId: maImageDocument?.ma_image_node_uuid ?? null,
    };
}

function findFirstUnlinkedImagePosBySrc(editor: Editor, pastedSrc: string): number | null {
    let foundPos: number | null = null;
    editor.state.doc.descendants((node, pos) => {
        if (foundPos !== null) {
            return false;
        }
        if (node.type.name !== 'image') {
            return;
        }
        const maImageId = node.attrs?.maImageId;
        if (typeof maImageId === 'number' && maImageId > 0) {
            return;
        }
        if (node.attrs?.src !== pastedSrc) {
            return;
        }
        foundPos = pos;
        return false;
    });
    return foundPos;
}

async function linkPastedLibraryImageFromUrlInner(editor: Editor, pastedSrc: string, documentId: number): Promise<void> {
    try {
        const { ma_image, ma_image_document } = await linkLibraryImageFromSrc(documentId, pastedSrc);
        if (editor.isDestroyed) {
            return;
        }

        const pos = findFirstUnlinkedImagePosBySrc(editor, pastedSrc);
        if (pos === null) {
            console.warn('Pasted library image node not found for linking:', pastedSrc);
            return;
        }

        const node = editor.state.doc.nodeAt(pos);
        if (!node || node.type.name !== 'image' || node.attrs?.src !== pastedSrc) {
            return;
        }

        const attrs = linkedMaImageNodeAttrs(ma_image, ma_image_document);
        const transaction = editor.state.tr.setNodeMarkup(pos, undefined, {
            ...node.attrs,
            ...attrs,
        });
        editor.view.dispatch(transaction);
    } catch (error) {
        console.error('Failed to link pasted library image:', error);
        toast.error('Failed to link pasted image', {
            description: error instanceof Error ? error.message : 'Unknown error',
        });
    }
}

export function linkPastedLibraryImageFromUrl(editor: Editor, pastedSrc: string, documentId: number): Promise<void> {
    const previous = linkPastedLibraryImageChainByEditor.get(editor) ?? Promise.resolve();
    const next = previous.catch(() => undefined).then(() => linkPastedLibraryImageFromUrlInner(editor, pastedSrc, documentId));
    linkPastedLibraryImageChainByEditor.set(editor, next);
    return next;
}

export function editorHasImageLibraryContext(editor: Editor): boolean {
    const { subjectName, releaseId } = editor.state.doc.attrs;
    return (
        typeof subjectName === 'string' &&
        subjectName.length > 0 &&
        releaseId !== null &&
        releaseId !== undefined &&
        String(releaseId).length > 0
    );
}

export function openImageLibraryPageInNewTab(editor: Editor): void {
    // TODO: fix this for new image library
    // const { subjectName, releaseId } = editor.state.doc.attrs;
    // if (!editorHasImageLibraryContext(editor)) {
    //     return;
    // }
    const url = '/images?subjectName=' + encodeURIComponent(editor.state.doc.attrs.subjectName as string);
    window.open(url, '_blank', 'noopener,noreferrer');
}

export async function insertImageFromLibrarySelection(editor: Editor, image: MaImage): Promise<boolean> {
    if (!editor.isEditable || !isNodeInSchema('image', editor)) {
        return false;
    }

    const documentId = editor.state.doc.attrs.documentId;
    const maImageDocument =
        typeof documentId === 'number' && documentId > 0 ? await linkImageToDocument(image.ma_image_id, documentId) : null;

    try {
        return editor
            .chain()
            .focus()
            .insertContent({
                type: 'image',
                attrs: linkedMaImageNodeAttrs(image, maImageDocument),
            })
            .run();
    } catch {
        return false;
    }
}

export const canSeeImageLibrary = (editor: Editor) => {
    const areImagesInSchema = isNodeInSchema('image', editor);
    const hasPermissionsToSeeImageLibrary = authService.canSeeAllMAContent();

    return areImagesInSchema && hasPermissionsToSeeImageLibrary;
};
